import { changelogData } from "@/config/changelog-data";

/**
 * Dynamic BA Brief Page — version, date, progress, recent changes
 * are auto-populated from changelog-data.ts. No manual updates needed.
 */

// --- Auto-sync from changelog ---
const latestRelease = changelogData[0];
const CURRENT_VERSION = latestRelease?.version || "3.7.0";
const LAST_UPDATE = latestRelease?.date
  ? new Date(latestRelease.date).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  : "27/02/2026";
const DOC_CODE = "APV-BA-2026-001";
const PROJECT_START = "01/12/2025";
const PROJECT_END = "30/04/2026";

// Calculate progress based on time elapsed
function calcProgress(): number {
  const start = new Date("2025-12-01").getTime();
  const end = new Date("2026-04-30").getTime();
  const now = Date.now();
  const p = Math.round(((now - start) / (end - start)) * 100);
  return Math.min(Math.max(p, 0), 100);
}

const PROGRESS = calcProgress();

// Get recent 5 releases for the "Lịch sử cập nhật" section
const recentReleases = changelogData.slice(0, 5);

export default function ProjectBriefPage() {
  return (
    <div
      style={{
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: "10pt",
        lineHeight: 1.6,
        color: "#334155",
        background: "#f1f5f9",
      }}
    >
      <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                .ba-doc { width: 100%; max-width: 210mm; margin: 20px auto; background: white; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
                .ba-page { position: relative; background: white; padding: 50px 70px; min-height: auto; border-bottom: 2px dashed #f1f5f9; }
                .ba-page h2 { font-size: 14pt; color: #1e40af; margin: 30px 0 15px; border-bottom: 1.5pt solid #f1f5f9; padding-bottom: 8px; }
                .ba-page h3 { font-size: 11pt; color: #0f172a; margin: 20px 0 10px; font-weight: 700; }
                .ba-page h4 { font-size: 10.5pt; color: #0f172a; margin: 15px 0 8px; font-weight: 700; }
                .ba-page p { margin-bottom: 12px; text-align: justify; }
                .ba-page table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 9.5pt; }
                .ba-page th, .ba-page td { border: 1px solid #e2e8f0; padding: 10px 12px; vertical-align: top; word-wrap: break-word; }
                .ba-page th { background: #f8fafc; color: #1e40af; font-weight: 700; text-align: left; }
                .ba-page tr:nth-child(even) { background: #fcfdfe; }
                .ba-page ul, .ba-page ol { margin: 10px 0 15px 25px; }
                .ba-page li { margin-bottom: 6px; }
                .ba-badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 8pt; font-weight: 700; text-transform: uppercase; }
                .ba-badge-primary { background: #dbeafe; color: #1e40af; }
                .ba-badge-success { background: #dcfce7; color: #166534; }
                .ba-badge-warning { background: #fef9c3; color: #854d0e; }
                .ba-info-box { background: #f8fafc; border-left: 4px solid #1e40af; padding: 15px 20px; margin: 20px 0; border-radius: 0 6px 6px 0; }
                .ba-flow-box { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 12px; margin: 25px 0; padding: 25px; background: #f8fafc; border-radius: 12px; border: 1px solid #f1f5f9; }
                .ba-flow-step { background: white; border: 1.5pt solid #1e40af; padding: 12px 18px; border-radius: 2px; font-size: 8.5pt; font-weight: 700; text-align: center; min-width: 110px; }
                .ba-flow-step.highlight { background: #1e40af; color: white; border-color: #1e3a8a; }
                .ba-flow-step.external { border-color: #d97706; background: #fffbeb; color: #92400e; }
                .ba-flow-arrow { font-size: 16pt; color: #94a3b8; }
                .ba-model-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin: 20px 0; }
                .ba-model-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; background: white; }

                .ba-cover { min-height: 297mm; display: flex; flex-direction: column; justify-content: space-between; text-align: center; padding: 80px; border-bottom: 6px solid #1e40af; }
                .ba-cover .company-logo { font-size: 38pt; font-weight: 800; color: #1e40af; margin-bottom: 10px; }
                .ba-cover .company-name { font-size: 14pt; color: #64748b; text-transform: uppercase; letter-spacing: 3px; font-weight: 500; }
                .ba-cover .doc-type { font-size: 12pt; color: #3b82f6; text-transform: uppercase; letter-spacing: 4px; font-weight: 700; margin-bottom: 20px; }
                .ba-cover .doc-title { font-size: 32pt; font-weight: 800; color: #0f172a; line-height: 1.2; margin-bottom: 15px; }
                .ba-cover .doc-subtitle { font-size: 16pt; color: #64748b; font-weight: 400; }

                .ba-cover-meta { background: #f8fafc; padding: 40px; border-radius: 12px; text-align: left; display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin: 60px 0; border: 1px solid #e2e8f0; }
                .ba-cover-meta label { font-size: 8.5pt; color: #64748b; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; display: block; margin-bottom: 5px; }
                .ba-cover-meta .val { font-size: 11pt; font-weight: 700; color: #0f172a; }

                .ba-signature-section { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-top: 60px; }
                .ba-signature-box { text-align: center; }
                .ba-signature-box .title { font-weight: 700; font-size: 8.5pt; margin-bottom: 80px; height: 35px; color: #334155; text-transform: uppercase; }
                .ba-signature-line { border-top: 1.5pt solid #0f172a; padding-top: 10px; font-size: 10pt; font-weight: 800; color: #0f172a; }

                .no-print-btn { position: fixed; bottom: 40px; right: 40px; z-index: 9999; background: #1e40af; color: white; border: none; padding: 16px 32px; border-radius: 50px; font-size: 12pt; font-weight: 700; cursor: pointer; box-shadow: 0 10px 25px rgba(30,64,175,0.4); display: flex; align-items: center; gap: 10px; }

                @media print {
                    @page { size: A4; margin: 0; }
                    body { background: white; padding: 0; margin: 0; }
                    .ba-doc { max-width: none; margin: 0; box-shadow: none; }
                    .ba-page { height: 297mm; padding: 15mm; page-break-after: always; border: none !important; }
                    .no-print-btn { display: none !important; }
                    nav, aside, header, .dock-nav { display: none !important; }
                }
            `}</style>

      <button className="no-print-btn" onClick={() => window.print()}>
        🖨️ In / Xuất PDF
      </button>

      <div className="ba-doc">
        {/* ═══════════════ PAGE 1: COVER ═══════════════ */}
        <div className="ba-page ba-cover">
          <div style={{ marginTop: "20mm" }}>
            <div style={{ marginBottom: 10 }}>
              <img
                src="/artpresto-logo.png"
                alt="APV Logo"
                style={{
                  height: 80,
                  objectFit: "contain",
                  margin: "0 auto",
                  display: "block",
                }}
              />
            </div>
            <div className="company-name">ARTPRESTO VIET NAM CO., LTD</div>
          </div>

          <div style={{ margin: "60px 0" }}>
            <div className="doc-type">Business Analysis Brief</div>
            <div className="doc-title">
              Hệ thống Quản lý
              <br />
              Sản xuất Bao bì
            </div>
            <div className="doc-subtitle">
              Packaging Production Management System
            </div>
          </div>

          <div className="ba-cover-meta">
            <div>
              <label>Mã tài liệu</label>
              <div className="val">{DOC_CODE}</div>
            </div>
            <div>
              <label>Phiên bản</label>
              <div className="val">{CURRENT_VERSION}</div>
            </div>
            <div>
              <label>Ngày phát hành</label>
              <div className="val">{PROJECT_START} (Start)</div>
            </div>
            <div>
              <label>Ngày cập nhật</label>
              <div className="val" style={{ color: "#1e40af" }}>
                {LAST_UPDATE}
              </div>
            </div>
            <div>
              <label>Trạng thái</label>
              <div className="val" style={{ color: "#059669" }}>
                ✅ Đang triển khai (Phase 4 - Go Live)
              </div>
            </div>
            <div>
              <label>Khách hàng</label>
              <div className="val">ARTPRESTO VIET NAM CO., LTD</div>
            </div>
            <div>
              <label>Đơn vị phát triển</label>
              <div className="val">EMS SOLUTION CO., LTD</div>
            </div>
          </div>

          <div
            style={{
              marginTop: "auto",
              paddingTop: 40,
              borderTop: "2px solid #e2e8f0",
              textAlign: "left",
            }}
          >
            <h4
              style={{
                fontSize: "10pt",
                color: "#0f172a",
                marginBottom: 15,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              ĐƠN VỊ PHÁT TRIỂN
            </h4>
            <p style={{ fontSize: "9.5pt", color: "#334155", marginBottom: 5 }}>
              <strong>CÔNG TY TNHH GIẢI PHÁP SỐ EMS</strong>
            </p>
            <p style={{ fontSize: "9.5pt", color: "#334155", marginBottom: 5 }}>
              MST: 0316870151 | Ngày cấp: 21/05/2021
            </p>
            <p style={{ fontSize: "9.5pt", color: "#334155", marginBottom: 5 }}>
              Địa chỉ: 93/4 Liên khu 5-6, Phường Bình Tân, TP. Hồ Chí Minh
            </p>
            <p style={{ fontSize: "9.5pt", color: "#334155", marginBottom: 5 }}>
              Đại diện: <strong>Nguyễn Tấn Hiền</strong> | Hotline: 0903 375 265
            </p>
            <p style={{ fontSize: "9.5pt", color: "#334155", marginBottom: 5 }}>
              Email: nguyentanhien87@gmail.com | Website: www.gpems.net
            </p>
          </div>
        </div>

        {/* ═══════════════ PAGE 2: EXECUTIVE DASHBOARD ═══════════════ */}
        <div className="ba-page">
          <h2 style={{ marginTop: 0, color: "#1e3a8a", borderBottom: "none" }}>
            BÁO CÁO TIẾN ĐỘ DỰ ÁN
          </h2>
          <p style={{ fontSize: "10pt", color: "#64748b", marginBottom: 25 }}>
            Ngày phát hành: <strong>{PROJECT_START}</strong> | Cập nhật lần
            cuối: <strong>{LAST_UPDATE}</strong> <br />
            Giai đoạn: <strong>Phase 4 - Go Live &amp; Hardening</strong>
          </p>

          {/* Timeline */}
          <div
            style={{
              background: "#f8fafc",
              padding: 20,
              borderRadius: 8,
              marginBottom: 25,
            }}
          >
            <h3
              style={{
                margin: "0 0 15px 0",
                fontSize: "12pt",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>📅 Timeline Dự án (05 Tháng)</span>
              <span>Tiến độ: {PROGRESS}%</span>
            </h3>
            <div
              style={{
                width: "100%",
                height: 12,
                background: "#e2e8f0",
                borderRadius: 6,
                overflow: "hidden",
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  width: `${PROGRESS}%`,
                  height: "100%",
                  background:
                    "linear-gradient(90deg, #1e40af 0%, #059669 100%)",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "9pt",
                color: "#64748b",
              }}
            >
              <span>Start: {PROJECT_START}</span>
              <span>Hôm nay: {LAST_UPDATE}</span>
              <span>End: {PROJECT_END}</span>
            </div>
          </div>

          {/* Stats */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 15,
              marginBottom: 25,
            }}
          >
            {[
              { val: "60", label: "Tables (PostgreSQL)", color: "#1e40af" },
              { val: "85", label: "Views (List/Detail)", color: "#059669" },
              { val: "28", label: "View Forms", color: "#d97706" },
              {
                val: `${PROGRESS}%`,
                label: "UI/UX Complete",
                color: "#1e3a8a",
              },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  border: "1px solid #e2e8f0",
                  padding: 15,
                  borderRadius: 8,
                  textAlign: "center",
                  background: "white",
                }}
              >
                <div
                  style={{ fontSize: "20pt", fontWeight: 700, color: s.color }}
                >
                  {s.val}
                </div>
                <div
                  style={{
                    fontSize: "8pt",
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Recent Changes — AUTO from changelog */}
          <h3
            style={{
              borderBottom: "2px solid #1e40af",
              paddingBottom: 5,
              marginBottom: 15,
            }}
          >
            Lịch sử Cập nhật Gần đây (Auto-sync từ Changelog)
          </h3>
          <table>
            <thead>
              <tr style={{ background: "#1e40af", color: "white" }}>
                <th style={{ padding: 10 }}>Version</th>
                <th style={{ padding: 10 }}>Ngày</th>
                <th style={{ padding: 10 }}>Nội dung</th>
              </tr>
            </thead>
            <tbody>
              {recentReleases.map((r, i) => (
                <tr key={i}>
                  <td>
                    <strong>v{r.version}</strong>
                  </td>
                  <td>{new Date(r.date).toLocaleDateString("vi-VN")}</td>
                  <td>
                    <strong>{r.title}</strong>
                    <br />
                    {r.changes.slice(0, 2).map((c, j) => {
                      const parts = c.split(/(\*\*.*?\*\*)/g);
                      return (
                        <span key={j}>
                          •{" "}
                          {parts.map((part, idx) => {
                            if (part.startsWith("**") && part.endsWith("**")) {
                              return (
                                <strong key={idx}>{part.slice(2, -2)}</strong>
                              );
                            }
                            return part;
                          })}
                          <br />
                        </span>
                      );
                    })}
                    {r.changes.length > 2 && (
                      <span style={{ color: "#64748b", fontSize: "8pt" }}>
                        +{r.changes.length - 2} thay đổi khác
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Key Note */}
          <div
            className="ba-info-box"
            style={{ marginTop: 20, borderLeftColor: "#d97706" }}
          >
            <strong>Lưu ý quan trọng:</strong> Hệ thống đã Go Live trên
            production VPS (HTTPS). Backend tRPC API + PostgreSQL hoạt động ổn
            định. Giai đoạn tiếp theo tập trung UAT và đào tạo nhân viên sử dụng
            hệ thống.
          </div>
        </div>

        {/* ═══════════════ PAGE 3: OVERVIEW ═══════════════ */}
        <div className="ba-page">
          <h2>1. Giới thiệu Tổng quan</h2>
          <h3>1.1. Mục tiêu Dự án</h3>
          <p>
            Xây dựng hệ thống quản lý sản xuất bao bì toàn diện cho{" "}
            <strong>ARTPRESTO VIET NAM CO., LTD (APV)</strong>, hỗ trợ từ khâu
            báo giá đến giao hàng, với khả năng linh hoạt cho cả sản xuất nội bộ
            và gia công ngoài.
          </p>

          <div className="ba-info-box">
            <strong>Chiến lược ISO 9001:2015:</strong> Lightning Fast, Premium
            UX - Áp dụng tư duy quản lý dựa trên rủi ro (Risk-based Thinking) và
            chu trình PDCA. Tối ưu hóa quy trình từ Xem xét Đơn hàng (8.2),
            Thiết kế & Phát triển (8.3) đến Kiểm soát Sản phẩm & Dịch vụ (8.5).
          </div>

          <h3>1.2. Các Bên Liên quan</h3>
          <table>
            <tbody>
              <tr>
                <th style={{ width: "30%" }}>Vai trò</th>
                <th>Đơn vị/Thông tin</th>
              </tr>
              <tr>
                <td>
                  <strong>Khách hàng (Chủ đầu tư)</strong>
                </td>
                <td>
                  ARTPRESTO VIET NAM CO., LTD (APV)
                  <br />• Mr. Kobayashi Yuichi - CEO
                  <br />• Mr. Vũ Hoàng Long - Director Manager
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Đơn vị Phát triển</strong>
                </td>
                <td>
                  EMS SOLUTION CO., LTD
                  <br />
                  MST: 0316870151
                  <br />• Nguyễn Tấn Hiền - CEO
                  <br />• Nguyễn Văn Hoà - CTO
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Liên hệ Dự án</strong>
                </td>
                <td>
                  Hotline: 0903 375 265 (Mr. Hiền) | Email:
                  nguyentanhien87@gmail.com
                </td>
              </tr>
            </tbody>
          </table>

          <h2>2. Phạm vi Dự án</h2>
          <h3>2.1. Phạm vi Bao gồm</h3>
          <table>
            <tbody>
              <tr>
                <th style={{ width: "25%" }}>Nhóm Module</th>
                <th>Chức năng Chính</th>
              </tr>
              <tr>
                <td>
                  <strong>Kinh doanh (Sales)</strong>
                </td>
                <td>
                  Quản lý Khách hàng, Báo giá, Đơn hàng bán (Sales Orders - SO),
                  Kiểm soát công nợ tín dụng, Theo dõi Giao hàng (Delivery)
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Kế hoạch (Planning)</strong>
                </td>
                <td>
                  Lệnh Sản xuất (Work Orders - WO), Tự động cấn trừ Lưu kho
                  (Stock Qty), Định mức Vật tư (BOM), Quy trình Sản xuất
                  (Routing)
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Sản xuất (Factory)</strong>
                </td>
                <td>
                  Nhật ký Sản xuất điện tử (Shop-floor Data Control), Trạng thái
                  Máy, Kiosk Công nhân mã vạch QR
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Gia công (Outsourcing)</strong>
                </td>
                <td>
                  Quản lý Nhà Gia công, Lệnh Gia công (Outsource PO), Phân bổ
                  vật tư hao hụt, Đối soát Công nợ
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Chất lượng (QC)</strong>
                </td>
                <td>
                  Phòng ngừa sự cố NCR, Kiểm tra Đầu ca (IQC), Kiểm tra Quá
                  trình (PQC), Kiểm tra Thành phẩm (OQC) chuẩn ISO
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Kho (Warehouse)</strong>
                </td>
                <td>
                  Nguyên liệu, Bán thành phẩm (WIP), Thành phẩm (FG), Kho Ảo.
                  <br />
                  <span className="text-emerald-600 font-medium">
                    (Mới: Hợp nhất Giao dịch Nhập/Xuất/Chuyển vào 1 giao diện duy nhất, tối ưu In Phiếu ISO 9001:2015).
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <h3>2.2. Phạm vi Loại trừ (Giai đoạn 1)</h3>
          <ul>
            <li>Tích hợp ERP/SAP bên ngoài</li>
            <li>Module Kế toán - Tài chính chi tiết</li>
            <li>Mobile App native (iOS/Android)</li>
          </ul>
        </div>

        {/* ═══════════════ PAGE 4: ARCHITECTURE ═══════════════ */}
        <div className="ba-page">
          <h2>3. Kiến trúc Hệ thống</h2>
          <h3>3.1. Công nghệ Sử dụng</h3>
          <table>
            <tbody>
              <tr>
                <th style={{ width: "25%" }}>Thành phần</th>
                <th>Công nghệ</th>
                <th style={{ width: "30%" }}>Mục đích</th>
              </tr>
              <tr>
                <td>
                  <strong>Frontend</strong>
                </td>
                <td>React + Vite + TypeScript</td>
                <td>Giao diện người dùng</td>
              </tr>
              <tr>
                <td>
                  <strong>UI Framework</strong>
                </td>
                <td>TailwindCSS + Shadcn UI</td>
                <td>Design System Premium</td>
              </tr>
              <tr>
                <td>
                  <strong>State Management</strong>
                </td>
                <td>TanStack Query + Router + Form</td>
                <td>Caching, Routing, Forms</td>
              </tr>
              <tr>
                <td>
                  <strong>Backend API</strong>
                </td>
                <td>Node.js + tRPC + Prisma</td>
                <td>Type-safe API Layer</td>
              </tr>
              <tr>
                <td>
                  <strong>Database</strong>
                </td>
                <td>PostgreSQL (VPS)</td>
                <td>Production Database</td>
              </tr>
            </tbody>
          </table>

          <h3>3.2. Kiến trúc Tổng quan</h3>
          <div className="ba-flow-box">
            <div className="ba-flow-step">
              Browser
              <br />
              <small>(React App)</small>
            </div>
            <div className="ba-flow-arrow">↔️</div>
            <div className="ba-flow-step highlight">
              TanStack Query
              <br />
              <small>(Cache Layer)</small>
            </div>
            <div className="ba-flow-arrow">↔️</div>
            <div className="ba-flow-step">
              tRPC API
              <br />
              <small>(Node.js)</small>
            </div>
            <div className="ba-flow-arrow">↔️</div>
            <div className="ba-flow-step external">
              PostgreSQL
              <br />
              <small>(VPS)</small>
            </div>
          </div>

          <h2>4. Mô hình Nghiệp vụ</h2>
          <h3>4.1. Quan hệ Dữ liệu Chính</h3>
          <div className="ba-info-box" style={{ borderLeftColor: "#059669" }}>
            <strong>Nguyên tắc quan trọng:</strong> Một Lệnh Sản xuất (LSX) có
            thể gộp nhiều Order Details từ các đơn hàng khác nhau có cùng sản
            phẩm. Điều này tối ưu hóa năng lực sản xuất và giảm thời gian chuyển
            đổi máy.
          </div>
          <table>
            <tbody>
              <tr>
                <th>Entity</th>
                <th>Quan hệ</th>
                <th>Mô tả</th>
              </tr>
              <tr>
                <td>
                  <strong>Customer</strong>
                </td>
                <td>1 → N</td>
                <td>
                  Một Khách hàng ERP (Customer) có nhiều đơn hàng và được set
                  hạn mức tín dụng tài chính.
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Order</strong>
                </td>
                <td>1 → N</td>
                <td>
                  Một Đơn hàng (Sales Order) chứa nhiều dòng sản phẩm (Line
                  Items) có kèm khai báo Tồn Lưu Kho (Stock Qty).
                </td>
              </tr>
              <tr>
                <td>
                  <strong>OrderDetail</strong>
                </td>
                <td>N → 1</td>
                <td>
                  Nhiều dòng sản phẩm có thể gộp vào 1 Lệnh Sản xuất chung để in
                  một lần (Batch Printing).
                </td>
              </tr>
              <tr>
                <td>
                  <strong>ProductionOrder (LSX)</strong>
                </td>
                <td>1 → N</td>
                <td>
                  Một Lệnh SX (WO) liên kết máy móc (Routing) và tự động tính
                  Nhu cầu Vật tư (MRP), cũng như tự động tạo Lệnh Chuẩn bị
                  Khuôn/Kẽm khi ban hành (F0).
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ═══════════════ PAGE 5: DEPLOYMENT MODELS ═══════════════ */}
        <div className="ba-page">
          <h2>5. Mô hình Triển khai Sản xuất (5 Scenarios)</h2>
          <p>
            Dựa trên đặc thù ngành bao bì B2B, hệ thống hỗ trợ{" "}
            <strong>5 kịch bản sản xuất/kinh doanh</strong> chuẩn để đảm bảo sự
            chặt chẽ giữa Nhu cầu (Sales) và Năng lực (Production):
          </p>

          <div className="ba-model-grid">
            {[
              {
                badge: "Trading",
                badgeCls: "ba-badge-warning",
                border: "#f59e0b",
                icon: "📦",
                title: "Model A",
                desc: "Outsource 100%. Không đầu tư máy móc.",
              },
              {
                badge: "Standard",
                badgeCls: "ba-badge-success",
                border: "#10b981",
                icon: "🏭",
                title: "Model B",
                desc: "Internal 100%. Tự chủ hoàn toàn.",
              },
              {
                badge: "Hybrid",
                badgeCls: "ba-badge-primary",
                border: "#3b82f6",
                icon: "🔄",
                title: "Model C",
                desc: "In NB + Gia công ngoài sau in.",
              },
              {
                badge: "Hybrid",
                badgeCls: "ba-badge-primary",
                border: "#3b82f6",
                icon: "🔁",
                title: "Model D",
                desc: "In ngoài + Gia công NB.",
              },
              {
                badge: "Contract",
                badgeCls: "",
                border: "#8e44ad",
                icon: "🔧",
                title: "Model E",
                desc: "Nhận gia công. Khách cấp vật tư.",
                badgeStyle: { background: "#ede9fe", color: "#6d28d9" },
              },
            ].map((m, i) => (
              <div
                key={i}
                className="ba-model-card"
                style={{ borderTop: `4px solid ${m.border}` }}
              >
                <span
                  className={`ba-badge ${m.badgeCls}`}
                  style={{
                    marginBottom: 8,
                    width: "fit-content",
                    ...(m.badgeStyle || {}),
                  }}
                >
                  {m.badge}
                </span>
                <h4>
                  {m.icon} {m.title}
                </h4>
                <p style={{ fontSize: "8.5pt", color: "#64748b" }}>{m.desc}</p>
              </div>
            ))}
          </div>

          <h3>5.1. So sánh chi tiết các Mô hình</h3>
          <table>
            <tbody>
              <tr>
                <th>Tiêu chí</th>
                <th>Model A (Trading)</th>
                <th>Model B (Internal)</th>
                <th>Model C (Hybrid 1)</th>
                <th>Model D (Hybrid 2)</th>
              </tr>
              <tr>
                <td>
                  <strong>In ấn</strong>
                </td>
                <td>
                  <span className="ba-badge ba-badge-warning">Outsource</span>
                </td>
                <td>
                  <span className="ba-badge ba-badge-success">Internal</span>
                </td>
                <td>
                  <span className="ba-badge ba-badge-success">Internal</span>
                </td>
                <td>
                  <span className="ba-badge ba-badge-warning">Outsource</span>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Gia công</strong>
                </td>
                <td>
                  <span className="ba-badge ba-badge-warning">Outsource</span>
                </td>
                <td>
                  <span className="ba-badge ba-badge-success">Internal</span>
                </td>
                <td>
                  <span className="ba-badge ba-badge-warning">Outsource</span>
                </td>
                <td>
                  <span className="ba-badge ba-badge-success">Internal</span>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Kiểm soát CL</strong>
                </td>
                <td>QC nhận hàng</td>
                <td>QC toàn trình</td>
                <td>QC In + Nhận GC</td>
                <td>QC GC + Nhận In</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ═══════════════ PAGE 6: ROADMAP & APPROVAL ═══════════════ */}
        <div className="ba-page">
          <h2>9. Lộ trình Triển khai Dự án</h2>
          <div className="ba-info-box" style={{ borderLeftColor: "#059669" }}>
            <strong>Thời gian thực hiện:</strong> 05 Tháng (Từ {PROJECT_START}{" "}
            đến {PROJECT_END})
          </div>

          <h3>9.1. Giai đoạn Phát triển Chuẩn (SDLC)</h3>
          <table>
            <tbody>
              <tr>
                <th style={{ width: "30%" }}>Giai đoạn</th>
                <th>Nội dung thực hiện chính</th>
              </tr>
              <tr>
                <td>
                  <strong>Tháng 1: Analysis & Architecture</strong>
                </td>
                <td>
                  Phân tích nghiệp vụ, lựa chọn Tech Stack, thiết kế cấu trúc dữ
                  liệu và kiến trúc hệ thống.
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Tháng 1-2: Pro UI/UX Design</strong>
                </td>
                <td>
                  Thiết kế giao diện chuẩn Pro Max UI, xây dựng Prototype tương
                  tác cao cấp.
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Tháng 2-3: Development & Integration</strong>
                </td>
                <td>
                  Phát triển các module chức năng (Frontend/Backend), tích hợp
                  API và dữ liệu thực.
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Tháng 3-4: QA & UAT Testing</strong>
                </td>
                <td>
                  Kiểm thử hệ thống (SIT/UAT), bắt lỗi, tối ưu hiệu năng và
                  nghiệm thu người dùng.
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Tháng 4: Deployment & PWA</strong>
                </td>
                <td>
                  Triển khai lên server, tối ưu PWA offline cho nhà xưởng và bàn
                  giao chính thức.
                </td>
              </tr>
            </tbody>
          </table>

          <h3>9.2. Roadmap Chi tiết các Phân hệ</h3>
          <table>
            <tbody>
              <tr>
                <th style={{ width: "15%" }}>Phase</th>
                <th>Nội dung</th>
                <th style={{ width: "20%" }}>Trạng thái</th>
              </tr>
              <tr>
                <td>
                  <strong>Phase 1-2</strong>
                </td>
                <td>
                  Foundation, Master Data, Sales (Quotation/Order), Engineering
                  (Tooling/TSKT), Outsourcing
                </td>
                <td>
                  <span className="ba-badge ba-badge-success">
                    ✓ Hoàn thành
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Phase 3</strong>
                </td>
                <td>
                  Sản xuất & Kho: Nhật ký SX, Real-time Monitoring, QC System,
                  Kho thành phẩm & Giao nhận
                  <br />
                  <small style={{ color: "#d97706" }}>
                    *Reboot: Đang chuẩn hóa Data Flow (Gộp lệnh/Combo) & System
                    Core
                  </small>
                </td>
                <td>
                  <span className="ba-badge ba-badge-success">
                    ✓ Hoàn thành
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Phase 4</strong>
                </td>
                <td>
                  Go Live: tRPC API + PostgreSQL trên VPS, Attachment Module, UX
                  Improvements, Print Preview, UAT & Đào tạo
                </td>
                <td>
                  <span className="ba-badge ba-badge-success">
                    ✓ Hoàn thành
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <h2>10. Phê duyệt Tài liệu</h2>
          <div className="ba-info-box" style={{ borderLeftColor: "#d97706" }}>
            <strong>Lưu ý quan trọng:</strong> Tài liệu này được cập nhật tự
            động theo tiến độ dự án. Phiên bản hiện tại: v{CURRENT_VERSION} (
            {LAST_UPDATE}). Hệ thống đã Go Live trên production VPS.
          </div>

          <p>
            Bằng việc ký tên dưới đây, các bên xác nhận đã đọc, hiểu và đồng ý
            với nội dung trong tài liệu Phân tích Nghiệp vụ này.
          </p>

          <div className="ba-signature-section">
            {[
              {
                title: "NGƯỜI LẬP",
                sub: "(EMS SOLUTION)",
                name: "Nguyễn Văn Hoà",
                role: "CTO",
              },
              {
                title: "NGƯỜI KIỂM TRA",
                sub: "(EMS SOLUTION)",
                name: "Nguyễn Tấn Hiền",
                role: "CEO",
              },
              {
                title: "PHÊ DUYỆT",
                sub: "(ARTPRESTO)",
                name: "Mr. Vũ Hoàng Long",
                role: "Director Manager",
              },
              {
                title: "PHÊ DUYỆT",
                sub: "(ARTPRESTO)",
                name: "Mr. Kobayashi Yuichi",
                role: "CEO",
              },
            ].map((s, i) => (
              <div key={i} className="ba-signature-box">
                <div className="title">
                  {s.title}
                  <br />
                  <small style={{ fontWeight: 400, color: "#64748b" }}>
                    {s.sub}
                  </small>
                </div>
                <div className="ba-signature-line">
                  <strong>{s.name}</strong>
                  <br />
                  {s.role}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 50,
              padding: 20,
              background: "#f8fafc",
              borderRadius: 8,
            }}
          >
            <h4 style={{ margin: "0 0 10px", color: "#334155" }}>
              Thông tin Liên hệ
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
                fontSize: "9.5pt",
              }}
            >
              <div>
                <strong>Đơn vị Phát triển:</strong>
                <br />
                EMS SOLUTION CO., LTD
                <br />
                Hotline: 0903 375 265 (Mr. Hiền)
                <br />
                Email: nguyentanhien87@gmail.com
              </div>
              <div>
                <strong>Thanh toán:</strong>
                <br />
                STK: 181119878
                <br />
                Ngân hàng ACB
                <br />
                CTK: Công ty TNHH Giải Pháp Số EMS
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
